import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'admin:theme';

function detectSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

interface ThemeState {
  /** Lo que el usuario eligió: light | dark | system (sigue el SO). */
  preference: Theme;
  /** Lo que se aplica realmente (resuelve "system" contra el SO). */
  resolved: 'light' | 'dark';
  setPreference: (t: Theme) => void;
}

/**
 * Store de tema. El ThemeProvider inicial aplica el tema al `<html>` y
 * escucha cambios en el system preference cuando preference === 'system'.
 */
export const useThemeStore = create<ThemeState>((set) => ({
  preference: readStoredTheme(),
  resolved: 'light', // se actualiza en ThemeProvider.mount
  setPreference: (t) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, t);
    }
    set({ preference: t });
  },
}));

/** Aplica el tema al DOM. Llamar desde el ThemeProvider y desde el listener de system preference. */
export function applyTheme(t: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = t;
}

/** Resuelve la preference final contra system preference. */
export function resolveTheme(preference: Theme): 'light' | 'dark' {
  if (preference === 'system') return detectSystemTheme();
  return preference;
}